import React, { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useI18n } from "@/i18n";
import api from "@/lib/api";
import { toast } from "sonner";
import { MagnifyingGlass, MapPin, XCircle, Star, ArrowRight, CircleNotch, Receipt } from "@phosphor-icons/react";

export default function Manage() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const [ref, setRef] = useState(params.get("ref") || "");
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [rating, setRating] = useState(0);

  const load = async (reference) => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get(`/bookings/${reference}`);
      setBooking(data);
    } catch (e) { setError(t("not_found")); setBooking(null); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (params.get("ref")) load(params.get("ref")); }, []); // eslint-disable-line

  const cancel = async () => {
    if (!window.confirm(t("cancel_confirm"))) return;
    try { await api.post(`/bookings/${booking.reference}/cancel`); toast.success(t("status_cancelled")); load(booking.reference); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const submitRating = async () => {
    await api.post(`/bookings/${booking.reference}/rate`, { rating });
    toast.success(t("thanks_rating")); load(booking.reference);
  };

  const requestInvoice = async () => {
    try { const { data } = await api.post(`/bookings/${booking.reference}/invoice`); toast.success(`${t("invoice_sent")} (${data.email})`); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const money = (v) => `CHF ${v.toFixed(2)}`;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="manage-page">
      <p className="font-body text-xs uppercase tracking-[0.24em] text-[#EF5A1E] mb-4">{t("nav_manage")}</p>
      <h1 className="font-heading font-light tracking-tighter text-5xl">{t("manage_title")}</h1>
      <p className="font-body text-[#5A607A] mt-4 text-lg">{t("manage_sub")}</p>

      <form onSubmit={(e) => { e.preventDefault(); load(ref.toUpperCase()); }} className="flex gap-3 mt-8">
        <input data-testid="manage-ref-input" value={ref} onChange={(e) => setRef(e.target.value)} placeholder={t("ref_ph")} className="flex-1 bg-white border border-[#E9E5DE] rounded-2xl px-4 py-3.5 font-body text-sm uppercase focus:border-[#EF5A1E] focus:ring-1 focus:ring-[#EF5A1E] focus:outline-none" />
        <button data-testid="manage-submit-btn" className="flex items-center gap-2 bg-[#EF5A1E] text-white font-body font-medium text-sm uppercase tracking-[0.14em] px-6 py-3.5 hover:bg-[#D2450F] transition-colors duration-300">
          {loading ? <CircleNotch size={16} className="animate-spin" /> : <MagnifyingGlass size={16} />} {t("lookup")}
        </button>
      </form>

      {error && <p className="font-body text-sm text-[#EF4444] mt-4" data-testid="manage-error">{error}</p>}

      {booking && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="border border-[#E9E5DE] mt-10 elev" data-testid="booking-details">
          <div className="flex items-center justify-between p-6 border-b border-[#E9E5DE] bg-[#F5F2ED]">
            <div>
              <p className="font-body text-[10px] uppercase tracking-widest text-[#5A607A]">{t("ref")}</p>
              <p className="font-body font-medium text-2xl tracking-tight text-[#EF5A1E]">{booking.reference}</p>
            </div>
            <span className={`font-body text-[10px] uppercase tracking-widest px-3 py-1.5 ${booking.status === "cancelled" ? "border border-[#EF4444] text-[#EF4444]" : "bg-[#EF5A1E] text-white"}`} data-testid="booking-status">
              {t(`status_${booking.status}`)}
            </span>
          </div>
          <div className="p-6 space-y-5">
            <div className="flex items-start gap-2.5">
              <MapPin size={18} className="mt-0.5 shrink-0 text-[#EF5A1E]" />
              <div className="font-body text-sm">
                <p className="font-semibold">{booking.pickup_name}</p>
                <p className="text-[#5A607A]">→ {booking.dropoff_name}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-5 font-body text-sm">
              <Info label={t("when")} value={`${booking.date} · ${booking.time}`} />
              <Info label={t("vehicle")} value={booking.vehicle_class} />
              <Info label={t("passengers_luggage")} value={`${booking.passengers}p · ${booking.luggage} ${t("bags")}`} />
              <Info label={t("fare")} value={`${money(booking.total)} (${booking.payment_method === "card" ? "Paid" : "On arrival"})`} />
            </div>

            <div className="flex flex-wrap gap-3 pt-5 border-t border-[#E9E5DE]">
              <Link to={`/track?ref=${booking.reference}`} className="flex items-center gap-2 bg-[#EF5A1E] text-white font-body font-medium text-sm uppercase tracking-[0.14em] px-6 py-3.5 hover:bg-[#D2450F] transition-colors duration-300">
                {t("track_your_ride")} <ArrowRight size={16} />
              </Link>
              <button data-testid="invoice-btn" onClick={requestInvoice} className="flex items-center gap-2 border border-[#0B1533] text-[#0B1533] font-body font-medium text-sm uppercase tracking-[0.14em] px-6 py-3.5 hover:bg-[#0B1533] hover:text-white transition-colors duration-300">
                <Receipt size={16} /> {t("request_invoice")}
              </button>
              {["confirmed", "assigned"].includes(booking.status) && (
                <button data-testid="cancel-btn" onClick={cancel} className="flex items-center gap-2 border border-[#EF4444] text-[#EF4444] font-body font-medium text-sm uppercase tracking-[0.14em] px-6 py-3.5 hover:bg-[#EF4444] hover:text-white transition-colors duration-300">
                  <XCircle size={16} /> {t("cancel_booking")}
                </button>
              )}
            </div>

            {booking.status === "completed" && !booking.rating && (
              <div className="border-t border-[#E9E5DE] pt-5" data-testid="rating-block">
                <p className="font-body text-sm font-semibold mb-3">{t("rate_title")}</p>
                <div className="flex gap-1.5 mb-4">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} onClick={() => setRating(n)} data-testid={`star-${n}`}>
                      <Star size={30} weight={n <= rating ? "fill" : "regular"} className={n <= rating ? "text-[#EF5A1E]" : "text-zinc-300"} />
                    </button>
                  ))}
                </div>
                <button data-testid="submit-rating-btn" disabled={!rating} onClick={submitRating} className="bg-[#EF5A1E] text-white font-body font-medium text-sm uppercase tracking-[0.14em] px-6 py-3.5 disabled:opacity-40 hover:bg-[#D2450F] transition-colors duration-300">
                  {t("submit_rating")}
                </button>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
}

const Info = ({ label, value }) => (
  <div>
    <p className="text-[10px] uppercase tracking-widest text-[#5A607A]">{label}</p>
    <p className="capitalize mt-1 font-medium">{value}</p>
  </div>
);
